<template>
    <y-modal
        ref="modal"
        :title="$t('general.schedule_demo_modal_title')"
        size="large"
        class="c-schedule-demo"
    >
        <template #body>
            <y-row
                v-if="content && configs && configs.commenting_submit"
                class="ma-0"
            >
                <y-col md="6">
                    <div class="c-schedule-demo__fields-wrapper">
                        <y-input
                            v-if="configs.commenting_name !== 'no_receive'"
                            v-model="model.name"
                            name="your_name"
                            :placeholder="$t('fields.your_name')"
                            class="mb-7"
                        />
                        <y-input
                            v-if="configs.commenting_email !== 'no_receive'"
                            v-model="model.email"
                            name="your_email"
                            :placeholder="$t('fields.your_email')"
                            class="mb-7"
                        />
                        <y-input
                            v-if="configs.commenting_company_name !== 'no_receive'"
                            v-model="model.company_name"
                            name="company_name"
                            :placeholder="$t('fields.company_name')"
                            class="mb-7"
                        />
                        <y-textarea
                            v-if="configs.commenting_text !== 'no_receive'"
                            v-model="model.text"
                            name="message"
                            class="mb-7"
                            rows="5"
                        />
                        <y-uploader
                            v-if="configs.commenting_attachment !== 'no_receive'"
                            ref="uploader"
                            v-model="model.attachment"
                            name="attachment"
                            :max-file-size="configs.commenting_attachment_max_capacity"
                            :accepted-files="configs.commenting_attachment_extensions"
                            :max-files="configs.commenting_attachment_max_number"
                            :payload="configs.payload"
                            linear
                            color="primary"
                        />
                    </div>
                </y-col>

                <y-col md="6">
                    <div class="c-schedule-demo__image-wrapper">
                        <img
                            v-if="content.image"
                            class="c-schedule-demo__image"
                            :src="imgSrc(content.image)"
                            :alt="content.title"
                        >
                    </div>
                </y-col>
            </y-row>

            <y-recaptcha
                ref="recaptcha"
                :site-key="configs.recaptcha.key"
                :size="configs.recaptcha.size"
                @verify="afterVerify"
            />
        </template>
        
        <template #footer>
            <y-button
                flat
                color="white"
                class="ml-5 cancel"
                @click.prevent="closeModal"
            >
                {{ $t('buttons.cancel') }}
            </y-button>
            <y-button
                flat
                color="primary"
                @click.prevent="beforeSubmit"
            >
                {{ $t('buttons.send_message') }}
            </y-button>
        </template>
    </y-modal>
</template>

<script>
    import {
        YInput, YTextarea, YUploader, YRecaptcha, YModal,
    } from '@groot-components/all';

    export default {
        name: 'ScheduleDemo',

        components: {
            YModal,
            YInput,
            YTextarea,
            YUploader,
            YRecaptcha,
        },

        /**
         * @inheritdoc
         */
        data() {
            return {
                content: null,
                configs: null,
                model  : {},
            };
        },

        /**
         * @inheritdoc
         */
        created() {
            this.fetch();
        },

        methods: {
            /**
             * Open Schedule Demo
             */
            open() {
                this.$refs.modal.open();
            },

            /**
             * Close Schedule Demo
             */
            closeModal() {
                this.$refs.modal.close();
            },

            /**
             * Check the Promise.all Request Error
             * 
             * @param {Array} req - Responses array
             * @param {index} index - number of response
             * @returns {boolean}
             */
            handleReq(req, index) {
                return req && req[index] && !req[index].isAxiosError;
            },

            /**
             * Fetch form config
             * 
             * @returns {*}
             */
            fetch() {
                this.$wait.end('fetch-schedule-demo');

                const content = this.$services.Content.postSingle({
                    type     : 'pages',
                    slug     : 'home1',
                    including: ['image'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const form = this.$services.Commenting.getFormConfig({
                    type  : 'pages',
                    slug  : 'home1',
                    locale: this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all([content, form]).then((responses) => {
                    if (this.handleReq(responses, 0)) {
                        this.content = responses[0].data.results;
                    }

                    if (this.handleReq(responses, 1)) {
                        this.configs = {
                            ...responses[1].data.results,
                            recaptcha: responses[1].data.metadata.recaptcha,
                            payload  : responses[1].data.metadata.payload,
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                }).finally(() => {
                    this.$wait.end('fetch-schedule-demo');
                });
            },

            /**
             * Verify recaptcha
             * Check recaptcha is active
             */
            beforeSubmit() {
                this.$wait.start('saving-schedule-demo');
                if (this.configs?.recaptcha?.active === '1') {
                    this.$refs.recaptcha.verify();
                } else {
                    this.save();
                }
            },

            /**
             * Set token and start Sending Comment
             *
             * @param {string} token
             */
            afterVerify(token) {
                this.model['g-recaptcha-response'] = token;
                this.save();
            },

            /**
             * Do send comment
             *
             * @returns {object}
             */
            save() {
                return this.$services.Commenting.commentSave({
                    ...this.model,
                    slug: 'home1',
                    type: 'pages',
                }).then(() => {
                    this.model = {};
                    this.closeModal();
                    this.$toast.success(this.$t('comments.message.success'));
                }).catch((error) => {
                    this.handleError(error);
                }).finally(() => {
                    this.$wait.end('saving-schedule-demo');
                    this.$refs.recaptcha?.reset();
                });
            },
        },
    };
</script>

<style lang="scss" src="./index.scss" />
